import { Box } from '@mui/material';
import MuiInputAdornment from '@mui/material/InputAdornment';
import { btnSize, btnType, Button, colors, Icons, Input } from '../../../../packages';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import cx from 'clsx';
import BoxFrame from '../../../../components/v2/BoxFrame';
import Header from '../../../../components/v2/Header';
import Typography from '../../../../components/v2/Typography';
import { typography } from '../../../../components/v2/Typography/index.constant';
import Heading from '../../../../components/v2/Heading';
import { HeadingLevel, HeadingWeight } from '../../../../components/v2/Heading/index.types';
import lockImg from '../../../../assets/images/lock.png';
import { insertIfObj } from '../../../../utils/InsertIf';
import { useDispatch, useSelector } from 'react-redux';
import { useStyles } from './index.styles';
import { validationSchema } from './index.schema';
import { getAuth } from '../../../../redux/modules/auth/selectors';
import { fetchCredentials, postCredentials } from '../../../../services/datalab/datalab.service';
import { showSnackbar } from '../../../../redux/modules/snackbar';

const defaultValues = {
  mdToolboxPracticeId: '',
  mdToolboxUsername: '',
  mdToolboxPassword: '',
  mdToolboxTokenPassword: '',
};

const MDToolboxAccess = ({ navItemStatus, setNavItemStatus, refresh, userId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isAdmin } = useSelector(getAuth);

  const [showUsername, setShowUsername] = useState(true);
  const [showPassword, setShowPassword] = useState(true);
  const [showPracticeId, setShowPracticeId] = useState(true);
  const [showTokenPassword, setShowTokenPassword] = useState(true);
  const [isEditable, setIsEditable] = useState(false);
  const [loader, setLoader] = useState(false);

  const { errors, values, handleChange, handleSubmit, touched, setFieldTouched, setFieldValue } = useFormik({
    initialValues: { ...defaultValues },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async payload => {
      if (isEditable) {
        setIsEditable(false);
        await setFieldValue('mdToolboxPassword', '');
        await setFieldValue('mdToolboxTokenPassword', '');
      } else {
        setLoader(true);
        try {
          const bodyRequest = {
            mdToolboxUsername: payload.mdToolboxUsername,
            mdToolboxPassword: payload.mdToolboxPassword,
            mdToolboxPracticeId: payload.mdToolboxPracticeId,
            mdToolboxTokenPassword: payload.mdToolboxTokenPassword,
            userAccountId: userId,
          };
          const response = await postCredentials(bodyRequest, { type: 'mdToolbox' });
          if (response.status === 200) {
            dispatch(
              showSnackbar({
                snackType: 'success',
                snackMessage: 'ePrescribe Credentials successfully validated',
              }),
            );
            setIsEditable(true);
            setLoader(false);
            refresh();
          } else {
            dispatch(
              showSnackbar({
                snackType: 'error',
                snackMessage: 'Error posting ePrescribe Credentials',
              }),
            );
            setLoader(false);
          }
        } catch (error) {
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: error?.data?.error.message || 'Something went wrong',
            }),
          );
          console.error('API call error:', error);
          setLoader(false);
        }
      }
    },
  });

  useEffect(() => {
    void getMdToolboxCredentials();
  }, []);

  const getMdToolboxCredentials = async () => {
    try {
      const response = await fetchCredentials({ userAccountId: userId }, { type: 'mdToolbox' });
      if (response?.status === 200 && response.data?.data?.mdToolboxUsername) {
        let { mdToolboxUsername, mdToolboxPracticeId } = response.data?.data;
        const dummyPassword = '*********';

        await setFieldValue('mdToolboxUsername', mdToolboxUsername);
        await setFieldValue('mdToolboxPassword', dummyPassword);
        await setFieldValue('mdToolboxPracticeId', mdToolboxPracticeId);
        await setFieldValue('mdToolboxTokenPassword', dummyPassword);
        setIsEditable(true);
        const state = navItemStatus;
        state.find(i => i.key === 'MDToolbox-access').status = 'success';
        setNavItemStatus([...state]);
      }
    } catch (err) {
      const msg = err.response?.data?.errors?.[0]?.endUserMessage || 'Something went wrong';
      console.log(msg);
      const state = navItemStatus;
      state.find(i => i.key === 'MDToolbox-access').status = 'disabled';
      setNavItemStatus([...state]);
    }
  };

  const onBlurInput = async e => {
    await setFieldTouched(e.target.name);
  };

  return (
    <>
      <Header label="ePrescribe Access" />
      <BoxFrame className={classes.card} sx={{ background: 'white' }}>
        <Box p={3}>
          <Box className={classes.information}>
            <img alt="lock" src={lockImg} className={classes.lockImg} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Heading
                variant={HeadingLevel.SUB}
                fontWeight={HeadingWeight.SEMI_BOLD}
                color={colors.success700}
              >
                {isAdmin ? "Provider's" : 'Your'} information is secure
              </Heading>
              <Typography {...typography.body.m.medium} color={colors.neutral700}>
                We&apos;ll need to access {isAdmin ? "the provider's" : 'your'} ePrescribe profile, so we can
                help update it with information related to {isAdmin ? "the provider's" : 'your'} participation
                in the Confidant Network. If we have any issues with accessing{' '}
                {isAdmin ? "the provider's" : 'your'} profile, we&apos;ll reach out to you.
              </Typography>
            </Box>
          </Box>
          <form onSubmit={handleSubmit}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 6,
                mb: 6,
                flexDirection: 'column',
                gap: 3,
              }}
            >
              <Box className={classes.section}>
                <Typography {...typography.body.normal.small.bold}>Username</Typography>
                <Input
                  className={cx(classes.input, {
                    [classes.error]:
                      !values.mdToolboxUsername && !!touched.mdToolboxUsername && !!errors.mdToolboxUsername,
                  })}
                  name="mdToolboxUsername"
                  placeholder="Enter your Username"
                  type={showUsername ? 'password' : 'text'}
                  onChange={handleChange}
                  onBlur={onBlurInput}
                  value={values.mdToolboxUsername || ''}
                  InputProps={{
                    ...insertIfObj(!!values.mdToolboxUsername && showUsername, {
                      endAdornment: (
                        <MuiInputAdornment position="end">
                          <Icons
                            glyph="eye-outlined"
                            className={classes.eye}
                            color={colors.neutral500}
                            onClick={() => setShowUsername(!showUsername)}
                          />
                        </MuiInputAdornment>
                      ),
                    }),
                    ...insertIfObj(
                      !values.mdToolboxUsername && !!touched.mdToolboxUsername && !!errors.mdToolboxUsername,
                      {
                        endAdornment: (
                          <MuiInputAdornment position="end">
                            <Icons
                              glyph="info"
                              className={classes.info}
                              color={colors.neutral500}
                              onClick={() => setShowUsername(!showUsername)}
                            />
                          </MuiInputAdornment>
                        ),
                      },
                    ),
                    ...insertIfObj(!!values.mdToolboxUsername && !showUsername, {
                      endAdornment: (
                        <MuiInputAdornment position="end">
                          <Icons
                            glyph="password-lock"
                            className={classes.eye}
                            color={colors.neutral500}
                            onClick={() => setShowUsername(!showUsername)}
                          />
                        </MuiInputAdornment>
                      ),
                    }),
                    readOnly: isEditable,
                  }}
                />
              </Box>
              <Box className={classes.section}>
                <Typography {...typography.body.normal.small.bold}>Practice ID</Typography>
                <Input
                  className={cx(classes.input, {
                    [classes.error]:
                      !values.mdToolboxPracticeId &&
                      !!touched.mdToolboxPracticeId &&
                      !!errors.mdToolboxPracticeId,
                  })}
                  name="mdToolboxPracticeId"
                  placeholder="Enter your Practice ID"
                  type={showPracticeId ? 'password' : 'text'}
                  onChange={handleChange}
                  onBlur={onBlurInput}
                  value={values.mdToolboxPracticeId || ''}
                  InputProps={{
                    ...insertIfObj(!!values.mdToolboxPracticeId && showPracticeId, {
                      endAdornment: (
                        <MuiInputAdornment position="end">
                          <Icons
                            glyph="eye-outlined"
                            className={classes.eye}
                            color={colors.neutral500}
                            onClick={() => setShowPracticeId(!showPracticeId)}
                          />
                        </MuiInputAdornment>
                      ),
                    }),
                    ...insertIfObj(
                      !values.mdToolboxPracticeId &&
                        !!touched.mdToolboxPracticeId &&
                        !!errors.mdToolboxPracticeId,
                      {
                        endAdornment: (
                          <MuiInputAdornment position="end">
                            <Icons
                              glyph="info"
                              className={classes.info}
                              color={colors.neutral500}
                              onClick={() => setShowPracticeId(!showPracticeId)}
                            />
                          </MuiInputAdornment>
                        ),
                      },
                    ),
                    ...insertIfObj(!!values.mdToolboxPracticeId && !showPracticeId, {
                      endAdornment: (
                        <MuiInputAdornment position="end">
                          <Icons
                            glyph="password-lock"
                            className={classes.eye}
                            color={colors.neutral500}
                            onClick={() => setShowPracticeId(!showPracticeId)}
                          />
                        </MuiInputAdornment>
                      ),
                    }),
                    readOnly: isEditable,
                  }}
                />
              </Box>
              <Box className={classes.section}>
                <Typography {...typography.body.normal.small.bold}>Password</Typography>
                <Input
                  className={cx(classes.input, {
                    [classes.error]:
                      !values.mdToolboxPassword && !!touched.mdToolboxPassword && !!errors.mdToolboxPassword,
                  })}
                  name="mdToolboxPassword"
                  placeholder="Enter your password"
                  type={showPassword ? 'password' : 'text'}
                  onChange={handleChange}
                  onBlur={onBlurInput}
                  value={values.mdToolboxPassword || ''}
                  InputProps={
                    !isEditable
                      ? {
                          ...insertIfObj(!!values.mdToolboxPassword && showPassword, {
                            endAdornment: (
                              <MuiInputAdornment position="end">
                                <Icons
                                  glyph="eye-outlined"
                                  className={classes.eye}
                                  color={colors.neutral500}
                                  onClick={() => setShowPassword(!showPassword)}
                                />
                              </MuiInputAdornment>
                            ),
                          }),
                          ...insertIfObj(!!values.mdToolboxPassword && !showPassword, {
                            endAdornment: (
                              <MuiInputAdornment position="end">
                                <Icons
                                  glyph="password-lock"
                                  className={classes.eye}
                                  color={colors.neutral500}
                                  onClick={() => setShowPassword(!showPassword)}
                                />
                              </MuiInputAdornment>
                            ),
                          }),
                          ...insertIfObj(
                            !values.mdToolboxPassword &&
                              !!touched.mdToolboxPassword &&
                              !!errors.mdToolboxPassword,
                            {
                              endAdornment: (
                                <MuiInputAdornment position="end">
                                  <Icons
                                    glyph="info"
                                    className={classes.info}
                                    color={colors.neutral500}
                                    onClick={() => setShowPassword(!showPassword)}
                                  />
                                </MuiInputAdornment>
                              ),
                            },
                          ),
                        }
                      : {
                          readOnly: isEditable,
                        }
                  }
                />
              </Box>
              <Box className={classes.section}>
                <Typography {...typography.body.normal.small.bold}>Token Password</Typography>
                <Input
                  className={cx(classes.input, {
                    [classes.error]:
                      !values.mdToolboxTokenPassword &&
                      !!touched.mdToolboxTokenPassword &&
                      !!errors.mdToolboxTokenPassword,
                  })}
                  name="mdToolboxTokenPassword"
                  placeholder="Enter your token password"
                  type={showTokenPassword ? 'password' : 'text'}
                  onChange={handleChange}
                  onBlur={onBlurInput}
                  value={values.mdToolboxTokenPassword || ''}
                  InputProps={
                    !isEditable
                      ? {
                          ...insertIfObj(!!values.mdToolboxTokenPassword && showTokenPassword, {
                            endAdornment: (
                              <MuiInputAdornment position="end">
                                <Icons
                                  glyph="eye-outlined"
                                  className={classes.eye}
                                  color={colors.neutral500}
                                  onClick={() => setShowTokenPassword(!showTokenPassword)}
                                />
                              </MuiInputAdornment>
                            ),
                          }),
                          ...insertIfObj(!!values.mdToolboxTokenPassword && !showTokenPassword, {
                            endAdornment: (
                              <MuiInputAdornment position="end">
                                <Icons
                                  glyph="password-lock"
                                  className={classes.eye}
                                  color={colors.neutral500}
                                  onClick={() => setShowTokenPassword(!showTokenPassword)}
                                />
                              </MuiInputAdornment>
                            ),
                          }),
                          ...insertIfObj(
                            !values.mdToolboxTokenPassword &&
                              !!touched.mdToolboxTokenPassword &&
                              !!errors.mdToolboxTokenPassword,
                            {
                              endAdornment: (
                                <MuiInputAdornment position="end">
                                  <Icons
                                    glyph="info"
                                    className={classes.info}
                                    color={colors.neutral500}
                                    onClick={() => setShowTokenPassword(!showTokenPassword)}
                                  />
                                </MuiInputAdornment>
                              ),
                            },
                          ),
                        }
                      : {
                          readOnly: isEditable,
                        }
                  }
                />
              </Box>
              <Box className={classes.btn}>
                {isEditable ? (
                  <Button
                    size={btnSize.SMALL}
                    onClick={handleSubmit}
                    variant={btnType.OUTLINE}
                    className={classes.outlineBtn}
                    disabled={loader}
                  >
                    {loader ? (
                      <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
                    ) : (
                      'Edit'
                    )}
                  </Button>
                ) : (
                  <Button
                    size={btnSize.SMALL}
                    disabled={
                      !values.mdToolboxPassword ||
                      !values.mdToolboxUsername ||
                      !values.mdToolboxTokenPassword ||
                      !values.mdToolboxPracticeId ||
                      loader
                    }
                    onClick={handleSubmit}
                  >
                    {loader ? (
                      <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
                    ) : (
                      'Save'
                    )}
                  </Button>
                )}
              </Box>
            </Box>
          </form>
        </Box>
      </BoxFrame>
    </>
  );
};

export default MDToolboxAccess;
